import { i18n } from '../utils/i18n';
import type { Airport, AirportGroup } from '@search/api/src/types';

// The schema models declare every field optional/nullable. The modal can only
// operate on entries that actually carry their identifying fields, so we derive
// "valid" types from the schema models (intersection, not redefinition) and
// enforce them at the constructor boundary via runtime filtering.
type ValidAirport = Airport & { code: string; name: string };
type ValidAirportGroup = Omit<AirportGroup, 'airports'> & {
  id: number;
  name: string;
  airports: ValidAirport[];
};

class AirportModal {
  private readonly airportGroups: ValidAirportGroup[];
  private readonly checked: Set<string> = new Set();
  private readonly expanded: Set<number> = new Set();
  private modal: any = null;
  private container: HTMLElement | null = null;
  private confirmed = false;
  private checkedSnapshot: Set<string> | null = null;
  private readonly onConfirm?: (
    selected: Array<{ code: string | number; name: string }>
  ) => void;

  constructor(
    airportGroups: AirportGroup[],
    onConfirm?: (
      selected: Array<{ code: string | number; name: string }>
    ) => void
  ) {
    this.airportGroups = airportGroups
      .filter(
        (g): g is AirportGroup & { id: number; name: string } =>
          g.id !== null && g.id !== undefined && g.name != null
      )
      .map(g => ({
        ...g,
        airports: (g.airports ?? []).filter(
          (a): a is ValidAirport => a.code != null && a.name != null
        ),
      }));
    this.expanded = new Set(this.airportGroups.map(x => x.id));
    this.onConfirm = onConfirm;
  }

  public open(): void {
    // Save state snapshot so we can restore on cancel/dismiss
    this.confirmed = false;
    this.checkedSnapshot = new Set(this.checked);

    const footer = this.createFooter();

    this.modal = new (window as any).Modal({
      title: i18n.t('modal.airport.title'),
      content: '<div id="airport-tree" role="tree"></div>',
      className: 'airport-search-title',
      footer: footer,
      padding: false,
      trackingLabel: 'searchComponent-airports',
      onClose: () => {
        if (!this.confirmed && this.checkedSnapshot) {
          // Restore state to what it was before the modal opened
          this.checked.clear();
          this.checkedSnapshot.forEach(code => this.checked.add(code));
        }
        this.checkedSnapshot = null;
      },
    }).open();

    if (this.modal) {
      const title = document.querySelector(
        '.airport-search-title .modal-title'
      ) as HTMLElement;
      if (title) {
        title.dataset.testid = 'airport-search-header';
      }
    }

    this.container = document.getElementById('airport-tree');
    if (this.container) {
      this.render();
    }
  }

  public close(): void {
    this.modal?.close();
  }

  public getSelection(): Array<{
    code: string | number;
    name: string;
    isGroup?: boolean;
  }> {
    const selected: Array<{
      code: string | number;
      name: string;
      isGroup?: boolean;
    }> = [];

    for (const group of this.airportGroups) {
      const checkedCount = group.airports.filter(a =>
        this.checked.has(a.code)
      ).length;
      if (checkedCount === group.airports.length) {
        selected.push({ code: group.id, name: group.name, isGroup: true });
      } else {
        group.airports.forEach(airport => {
          if (this.checked.has(airport.code)) {
            selected.push({ code: airport.code, name: airport.name });
          }
        });
      }
    }

    return selected;
  }

  /** Whether `code` is an airport group id or airport code this modal offers. */
  public offers(code: string): boolean {
    return this.airportGroups.some(
      group =>
        group.id.toString() === code ||
        group.airports.some(airport => airport.code === code)
    );
  }

  public setSelection(codes: string[]): void {
    this.checked.clear();
    codes.forEach(code => {
      const group = this.airportGroups.find(g => g.id.toString() === code);
      if (group) {
        group.airports.forEach(airport => this.checked.add(airport.code));
      } else {
        this.checked.add(code);
      }
    });
    if (this.container) {
      this.render();
    }
  }

  private createFooter(): HTMLElement {
    const container = document.createElement('div');
    container.className = 'flex gap-3 justify-around';

    const clearBtn = document.createElement('button');
    clearBtn.className = 'btn btn-secondary btn-lg grow';
    clearBtn.textContent = i18n.t('modal.common.clearAll');
    clearBtn.dataset.action = 'clear';
    clearBtn.dataset.testid = 'search-modal-clearall';
    clearBtn.addEventListener('click', () => {
      this.confirmed = true;
      this.setSelection([]);
      this.onConfirm?.(this.getSelection());
    });

    const confirmBtn = document.createElement('button');
    confirmBtn.className = 'btn btn-primary btn-lg grow';
    confirmBtn.textContent = i18n.t('modal.common.confirm');
    confirmBtn.dataset.action = 'confirm';
    confirmBtn.dataset.testid = 'search-modal-confirm';
    confirmBtn.addEventListener('click', () => {
      this.confirmed = true;
      this.onConfirm?.(this.getSelection());
      this.close();
    });

    container.appendChild(clearBtn);
    container.appendChild(confirmBtn);
    return container;
  }

  private render(): void {
    if (!this.container) return;

    const activeElement = document.activeElement as HTMLElement;
    const focusedDestinationId = activeElement
      ?.closest('[data-airport-id]')
      ?.getAttribute('data-airport-id');
    const focusedElementType = activeElement?.tagName.toLowerCase();
    const shouldRestoreFocus = this.container.contains(activeElement);

    this.container.innerHTML = '';

    const anyAirport = this.createAnyAirportItem();
    this.container.appendChild(anyAirport);

    this.airportGroups.forEach(group => {
      this.container!.appendChild(this.createGroupItem(group));
    });

    if (shouldRestoreFocus && focusedDestinationId) {
      const newElement = this.container.querySelector(
        `[data-airport-id="${focusedDestinationId}"] ${focusedElementType}`
      ) as HTMLElement;
      newElement?.focus();
    }
  }

  private createAnyAirportItem(): HTMLElement {
    const isChecked = this.checked.size === 0;
    const item = document.createElement('div');
    item.setAttribute('role', 'treeitem');
    item.dataset.airportId = 'any';

    const content = document.createElement('div');
    content.className = `form-label flex items-center w-full tt-body-md p-4 border-b border-component ${isChecked ? 'bg-functional-tint cursor-not-allowed' : 'cursor-pointer'}`;

    const checkbox = document.createElement('input');
    checkbox.className = 'form-checkbox mr-2';
    checkbox.type = 'checkbox';
    checkbox.checked = isChecked;
    checkbox.disabled = isChecked;
    if (isChecked)
      checkbox.classList.add(
        'border-primary',
        'bg-primary',
        'cursor-not-allowed'
      );

    checkbox.addEventListener('change', () => {
      if (checkbox.checked) {
        this.checked.clear();
        this.render();
      }
    });

    const label = document.createElement('label');
    label.textContent = i18n.t('search.anyAirport');
    label.className = isChecked ? 'cursor-not-allowed' : 'cursor-pointer';
    label.dataset.testid = 'airport-checkbox-any';

    content.appendChild(checkbox);
    content.appendChild(label);
    content.addEventListener('click', e => {
      if (e.target !== checkbox) {
        this.checked.clear();
        this.render();
      }
    });

    item.appendChild(content);
    return item;
  }

  private createGroupItem(group: ValidAirportGroup): HTMLElement {
    const isExpanded = this.expanded.has(group.id);
    const checkedCount = group.airports.filter(a =>
      this.checked.has(a.code)
    ).length;
    const allChecked = checkedCount === group.airports.length;
    const someChecked = checkedCount > 0 && !allChecked;

    const item = document.createElement('div');
    const content = document.createElement('div');
    content.className = `flex items-center form-label w-full tt-body-md py-2 pr-2 pl-4 border-b border-component ${allChecked || someChecked ? 'bg-functional-tint' : ''}`;
    content.dataset.airportId = group.id.toString();
    content.dataset.testid = `airport-group-checkbox-${group.id.toString()}`;

    const checkboxContainer = document.createElement('div');
    checkboxContainer.classList.add('flex', 'items-center', 'relative', 'mr-2');

    const checkbox = document.createElement('input');
    checkbox.className = 'form-checkbox';
    checkbox.type = 'checkbox';
    checkbox.checked = allChecked;

    if (someChecked) {
      checkbox.indeterminate = true;
      checkbox.classList.add('border-primary', 'bg-primary');
    } else if (allChecked) {
      checkbox.classList.add('border-primary', 'bg-primary');
    }

    checkbox.addEventListener('change', () => {
      if (checkbox.checked) {
        group.airports.forEach(a => this.checked.add(a.code));
      } else {
        group.airports.forEach(a => this.checked.delete(a.code));
      }
      this.render();
    });

    checkboxContainer.appendChild(checkbox);

    if (someChecked && checkedCount > 0) {
      const countBadge = document.createElement('span');
      countBadge.className = 'text-secondary text-xs font-bold';
      countBadge.style.position = 'absolute';
      countBadge.style.left = '50%';
      countBadge.style.transform = 'translate(-50%, 0)';
      countBadge.style.pointerEvents = 'none';
      countBadge.textContent = checkedCount.toString();
      checkboxContainer.appendChild(countBadge);
    }

    const label = document.createElement('label');
    label.textContent = group.name;
    label.className = 'cursor-pointer flex-1';
    label.id = `label-search-modal-airport-${group.id}`;

    checkbox.setAttribute('aria-labelledby', label.id);

    content.appendChild(checkboxContainer);
    content.appendChild(label);
    content.addEventListener('click', e => {
      if (e.target !== checkbox) {
        e.preventDefault();
        const newChecked = !checkbox.checked;
        if (newChecked) {
          group.airports.forEach(a => this.checked.add(a.code));
        } else {
          group.airports.forEach(a => this.checked.delete(a.code));
        }
        this.render();
      }
    });

    item.appendChild(content);

    if (group.airports.length > 0) {
      const expandBtn = document.createElement('button');

      expandBtn.className = 'p-2';
      expandBtn.dataset.testid = `airport-group-expand-${group.id}`;
      expandBtn.addEventListener('click', e => {
        e.stopPropagation();
        this.toggleExpand(group.id);
      });

      const arrowSvg = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'svg'
      );
      arrowSvg.classList.add('icon', 'icon-sm', 'icon-dark');

      const arrowSvgUse = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'use'
      );
      arrowSvgUse.setAttribute(
        'href',
        isExpanded ? '#chevron-up' : '#chevron-down'
      );

      arrowSvg.appendChild(arrowSvgUse);
      expandBtn.appendChild(arrowSvg);
      content.appendChild(expandBtn);

      const childrenContainer = document.createElement('div');

      if (!isExpanded) {
        childrenContainer.classList.add('hidden');
      }

      group.airports.forEach(airport => {
        childrenContainer.appendChild(this.createAirportItem(airport));
      });
      item.appendChild(childrenContainer);
    }

    return item;
  }

  private toggleExpand(id: number): void {
    this.expanded.has(id) ? this.expanded.delete(id) : this.expanded.add(id);
    this.render();
  }

  private createAirportItem(airport: ValidAirport): HTMLElement {
    const isChecked = this.checked.has(airport.code);
    const item = document.createElement('div');
    const content = document.createElement('div');
    content.className = `flex items-center form-label w-full tt-body-md pr-2 border-b border-component py-4 pl-8 ${isChecked ? 'bg-functional-tint' : ''}`;
    content.dataset.airportId = airport.code.toString();
    content.dataset.testid = `airport-checkbox-${airport.code.toString()}`;

    const checkbox = document.createElement('input');
    checkbox.className = 'form-checkbox mr-2';
    checkbox.type = 'checkbox';
    checkbox.checked = isChecked;
    if (isChecked) checkbox.classList.add('border-primary', 'bg-primary');

    checkbox.addEventListener('change', () => {
      if (checkbox.checked) {
        this.checked.add(airport.code);
      } else {
        this.checked.delete(airport.code);
      }
      this.render();
    });

    const label = document.createElement('label');
    label.textContent = airport.name;
    label.className = 'cursor-pointer flex-1';
    label.id = `label-search-modal-airport-${airport.code}`;
    checkbox.setAttribute('aria-labelledby', label.id);

    content.appendChild(checkbox);
    content.appendChild(label);
    content.addEventListener('click', e => {
      if (e.target !== checkbox) {
        e.preventDefault();
        if (this.checked.has(airport.code)) {
          this.checked.delete(airport.code);
        } else {
          this.checked.add(airport.code);
        }
        this.render();
      }
    });

    item.appendChild(content);
    return item;
  }
}

export default AirportModal;
